// Corner-anchoring geometry for the DOIT overlay.
// Ports core/renderer/doit_overlay/positions.py.

export type Position = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export const POSITION_CHOICES: readonly Position[] = [
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
] as const;

export const POSITION_ALIASES: Record<string, Position> = {
  tl: "top-left",
  tr: "top-right",
  bl: "bottom-left",
  br: "bottom-right",
};

export const POS_ABBR: Record<Position, string> = {
  "top-left": "tl",
  "top-right": "tr",
  "bottom-left": "bl",
  "bottom-right": "br",
};

/**
 * Top-left (x, y) for an element anchored at one of the four corners.
 * Padding is computed against the canvas SHORT edge so the visual
 * margin stays consistent regardless of canvas aspect.
 */
export function anchorXY(
  canvasW: number,
  canvasH: number,
  elemW: number,
  elemH: number,
  position: Position,
  padXFrac: number,
  padYFrac: number,
): { x: number; y: number } {
  const shortEdge = Math.min(canvasW, canvasH);
  const padX = Math.round(shortEdge * padXFrac);
  const padY = Math.round(shortEdge * padYFrac);

  switch (position) {
    case "top-left":
      return { x: padX, y: padY };
    case "top-right":
      return { x: canvasW - elemW - padX, y: padY };
    case "bottom-left":
      return { x: padX, y: canvasH - elemH - padY };
    case "bottom-right":
      return { x: canvasW - elemW - padX, y: canvasH - elemH - padY };
  }
}

export function normalisePosition(value: string): Position {
  const lower = value.toLowerCase().replace(/_/g, "-");
  if (lower in POSITION_ALIASES) {
    return POSITION_ALIASES[lower]!;
  }
  if (POSITION_CHOICES.includes(lower as Position)) {
    return lower as Position;
  }
  throw new Error(
    `position must be one of ${POSITION_CHOICES.join("/")} (or tl/tr/bl/br); got ${JSON.stringify(value)}`,
  );
}
